/**
 * As this file is reused in several other files, try to keep it lean and small.
 * Importing other npm packages here could lead to needlessly increasing the client bundle size, or end up in a server-only function that don't need it.
 */

import { sanityDatasetForHostname } from "@/lib/preview-hostname"

/**
 * PUBLIC: Minimist environment
 */
type MinimistEnv = 'prod' | 'staging' | 'dev' | 'test'
export const MINIMIST_ENV: MinimistEnv = (process.env.NEXT_PUBLIC_MINIMIST_ENV || 'prod') as MinimistEnv

/**
 * Typesense search only API key
 *
 * The default is PRODUCTION's key. See the warning under TYPESENSE_HOST.
 */
export const TYPESENSE_SEARCH_ONLY_API_KEY = process.env.TYPESENSE_SEARCH_ONLY_API_KEY || 'MnMsjnWhFz6h6BHo9ItVvFkKcwR07Fon'

/**
 * Typesense host
 *
 * The default is PRODUCTION's cluster, and the key default above is production's
 * key — so an environment that sets NEITHER reads prod's index successfully and
 * silently. That is not a hypothetical: staging had no TYPESENSE_* vars at all,
 * so `/store/<handle>` searched prod for a staging store id, found nothing, and
 * rendered "This shop hasn't listed anything yet" over a shop whose stock was
 * entirely correct (MIN-2657, following #2822 which moved listing-grpc and
 * minimist-xl but not this app).
 *
 * The defaults stay — prod and recover set nothing and must keep working — so
 * the fallback announces itself instead. See `warnOnProdTypesenseFallback`.
 */
export const TYPESENSE_HOST = process.env.TYPESENSE_HOST || 'typesense-api.mnm.st'

/**
 * Say so, once at boot, when a non-prod deployment is about to read production.
 *
 * Server-only: `TYPESENSE_HOST` is not a `NEXT_PUBLIC_` var, so it is stripped
 * from the client bundle and would look "unset" in every browser — the guard is
 * what stops this from firing on every page view. `MINIMIST_ENV` is read from
 * the real runtime env here for the same reason.
 *
 * A warning rather than a throw: this module is imported by nearly every route,
 * so throwing would turn a misconfigured search index into a fully dead site.
 * The two states below are both silently-wrong-but-alive, which is exactly the
 * failure mode that needs a line in the log.
 */
function warnOnProdTypesenseFallback(): void {
  if (typeof window !== 'undefined') return
  if (MINIMIST_ENV === 'prod') return

  if (!process.env.TYPESENSE_HOST) {
    console.warn(
      `[env] TYPESENSE_HOST is unset in MINIMIST_ENV=${MINIMIST_ENV} — falling back to the PRODUCTION index (${TYPESENSE_HOST}). ` +
        'Searches will succeed and return nothing for this environment\'s ids. ' +
        'Set TYPESENSE_HOST and TYPESENSE_SEARCH_ONLY_API_KEY together in this deployment.',
    )
  } else if (!process.env.TYPESENSE_SEARCH_ONLY_API_KEY) {
    // Keys are cluster-scoped, so this combination always 403s.
    console.warn(
      `[env] TYPESENSE_HOST is set to ${TYPESENSE_HOST} but TYPESENSE_SEARCH_ONLY_API_KEY is unset, ` +
        'so the PRODUCTION key is being sent to a non-production cluster. Every search will be Forbidden.',
    )
  }
}

warnOnProdTypesenseFallback()

/**
 * Typesense port
 */
export const TYPESENSE_PORT = process.env.TYPESENSE_PORT || '443'

/**
 * Typesense protocol
 */
export const TYPESENSE_PROTOCOL = process.env.TYPESENSE_PROTOCOL || 'https'


/**
 * Sanity API read token
 */
export const SANITY_API_READ_TOKEN = process.env.SANITY_API_READ_TOKEN ?? process.env.NEXT_PUBLIC_SANITY_API_READ_TOKEN

/**
 * Sanity dataset
 *
 * Resolved at module load. NEXT_PUBLIC_* env vars are inlined into both the
 * client and server bundles by Next.js at `next build`, so a runtime pod env
 * value does not propagate. Instead:
 *   - Client (e.g. Sanity Studio): derive from window hostname.
 *   - Server: read the non-prefixed SANITY_DATASET (not inlined).
 */
function resolveSanityDataset(): string {
  // Keep this shape: server branch first, client branch as a single returned
  // call. The previous form — `if (typeof window !== 'undefined') { const
  // hostname = stripPreviewEnvLabel(...); return ... }` — is miscompiled by
  // Next 15.5's SWC minifier in the CLIENT bundle: once `typeof window` is
  // folded to "object", it inlines the call into the block-scoped const and
  // reduces the whole function to `void 0`. Every browser-side Sanity client
  // (the embedded Studio included) then got `dataset: undefined`, and Studio
  // crashed with "`dataset` must be provided to perform queries".
  if (typeof window === 'undefined') {
    return process.env.SANITY_DATASET || 'production'
  }
  // A per-PR preview is served at pr-<n>[-<target>].staging.mnm.st, which does
  // NOT start with `staging.` — so the helper strips that label first; without
  // it, Sanity Studio on a preview would edit the PRODUCTION dataset while the
  // same pod server-renders `staging` (SANITY_DATASET is set in k8s/preview,
  // MIN-4503).
  return sanityDatasetForHostname(window.location?.hostname ?? '')
}
export const SANITY_DATASET = resolveSanityDataset()

/**
 * Sanity project ID
 */
export const SANITY_PROJECT_ID = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || 'coak1kyy'

/**
 * see https://www.sanity.io/docs/api-versioning for how versioning works
 */
export const SANITY_API_VERSION = process.env.NEXT_PUBLIC_SANITY_API_VERSION || "2024-02-28"

/**
 * Shared secret required on the Help Center feed (/api/help). Server-only (never
 * NEXT_PUBLIC). Unlike the public changelog, the help feed serves in-app content
 * that must not be openly fetchable, so the Minimist XL proxy calls it with this
 * bearer and the route 401s without it. Empty in local dev — the XL proxy then
 * sends an empty bearer too, so it still round-trips locally.
 */
export const HELP_FEED_SECRET = process.env.HELP_FEED_SECRET ?? ''

/**
 * Slack bot token (`xoxb-…`) used by /api/contact to post inbound website leads
 * to the sales channel. Server-only (never NEXT_PUBLIC) — a leaked bot token can
 * post as Minimist anywhere the bot is a member.
 *
 * Unset means the contact form and download gate cannot deliver a lead, so the
 * route answers 502 and logs the full lead rather than reporting success for a
 * message nobody received (MIN-5065).
 */
export const SLACK_API_TOKEN = process.env.SLACK_API_TOKEN ?? ''

/**
 * Slack channel that inbound website leads land in. Defaults to the SAME channel
 * id Medusa's `notify-slack-on-prospect` subscriber posted to (#sales), so the
 * move off Medusa changes nothing for the people reading them.
 */
export const CONTACT_SLACK_CHANNEL = process.env.CONTACT_SLACK_CHANNEL || 'C06PZ9ZFNBT'

/**
 * Slack channel that newly published What's New entries are announced in, for
 * internal team awareness (MIN-6159; #whats-new is C0C6ADGSU9F). Deliberately
 * NO default: only the production overlay sets it. Staging publishes test
 * entries constantly, so there an unset channel makes the announcer a logged
 * no-op instead of spam.
 */
export const WHATS_NEW_SLACK_CHANNEL = process.env.WHATS_NEW_SLACK_CHANNEL ?? ''

/**
 * Mini's Slack bot token (`xoxb-…`, the openclaw app — the same value as
 * `slack-bot-token` in infra/openclaw's secret). What's New announcements post
 * as Mini, not as the contact-form bot behind SLACK_API_TOKEN. Server-only.
 * Unset ⇒ the announcer answers 503 so Sanity retries once it is configured.
 */
export const WHATS_NEW_SLACK_TOKEN = process.env.WHATS_NEW_SLACK_TOKEN ?? ''

/**
 * Signing secret of the Sanity GROQ webhook that calls
 * /api/changelog/slack-webhook when a changelogEntry is created. Server-only.
 * Unset ⇒ the route refuses every delivery with 503 (fails closed).
 */
export const SANITY_CHANGELOG_WEBHOOK_SECRET = process.env.SANITY_CHANGELOG_WEBHOOK_SECRET ?? ''

/**
 * HubSpot portal and form that inbound website leads are also submitted to, so
 * they become CRM contacts with a "Minimist: <form name>" first conversion
 * (MIN-5900). Neither is a secret: both appear in any embedded HubSpot form.
 *
 * The form must contain the fields `email`, `firstname`, `lastname`, `company`,
 * `phone` and `message` (see `hubspotFields`). As of 2026-10 HubSpot answers a
 * field the form lacks with a 200 and silently drops its value, but it has
 * announced rejecting such submissions (`FIELD_NOT_IN_FORM_DEFINITION`), at which
 * point the whole HubSpot record would fail (logged; Slack unaffected). We send no
 * `legalConsentOptions`, so turning on a GDPR consent requirement for that form
 * in HubSpot makes every submission 400 (logged as `console.error`; Slack still
 * delivers). Setting the guid to an empty string skips the HubSpot submission
 * with a warn line. The portal id uses `||` because an empty one is never valid.
 */
export const HUBSPOT_PORTAL_ID = process.env.HUBSPOT_PORTAL_ID || '143576341'
// "Contact enquiry" (Marketing → Forms in the HubSpot portal).
export const HUBSPOT_CONTACT_FORM_GUID = process.env.HUBSPOT_CONTACT_FORM_GUID ?? '445216f7-3c78-443c-97ed-12ef7cffa297'
// Data-centre region of that portal. Hero Journey V2's demo requests
// (POST /api/hero-demo-request) submit to the regional forms API with it.
export const HUBSPOT_REGION = process.env.HUBSPOT_REGION || 'eu1'

/**
 * PUBLIC: Used to configure edit intent links, for Presentation Mode, as well as to configure where the Studio is mounted in the router.
 * 
 * Value similar to '/studio'
 */
export const SANITY_STUDIO_PATHNAME = process.env.NEXT_PUBLIC_SANITY_STUDIO_PATHNAME || "/studio"

/**
 * PUBLIC: URL to the Studio, used to configure edit intent links, for Presentation Mode.
 * 
 * Value similar to 'http://localhost:3000/studio'
 */
export const SANITY_STUDIO_URL = process.env.NEXT_PUBLIC_SANITY_STUDIO_URL || `http://localhost:3000${SANITY_STUDIO_PATHNAME}`

/**
 * PUBLIC: Use live API
 */
export const SANITY_USE_LIVE_API = process.env.NEXT_PUBLIC_SANITY_USE_LIVE_API === 'true'


/**
 * PUBLIC: Root domain
 */
export const ROOT_DOMAIN = process.env.NEXT_PUBLIC_ROOT_DOMAIN || 'mnm.st'

export const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || `https://${ROOT_DOMAIN}`

/**
 * PUBLIC: Debug mode
 */
export const DEBUG = process.env.NEXT_PUBLIC_DEBUG === 'true'

/**
 * PUBLIC: Show careers page
 * 
 * @default false Will show careers page on the site
 */
export const SHOW_CAREERS_PAGE = process.env.NEXT_PUBLIC_SHOW_CAREERS_PAGE === 'true'

/**
 * PUBLIC: .com shows the search engine - not our B2B site
 * @default true Show search engine on .com
 */
export const SHOW_SEARCH_ENGINE_ON_COM = process.env.NEXT_PUBLIC_SHOW_SEARCH_ENGINE_ON_COM !== 'false'

/**
 * PUBLIC: suffix for preview deployments
 */
export const PREVIEW_DEPLOYMENT_SUFFIX = process.env.NEXT_PUBLIC_PREVIEW_DEPLOYMENT_SUFFIX


/**
 * PUBLIC: Unsplash access key
 */
export const UNSPLASH_ACCESS_KEY = process.env.NEXT_PUBLIC_UNSPLASH_ACCESS_KEY || 'spC1YiBFZHM6btDmHs7BARXrAflIeiC-Oxxg3uzlnBM'

/**
 * PUBLIC: Unsplash API URL
 */
export const UNSPLASH_API_URL = 'https://api.unsplash.com'

/**
 * Google Maps API key
 */
export const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || 'AIzaSyDMC8U2iFRzggSiAFyeyorY9JYFvmJq564'

// Add tracking related environment variables
export const ENABLE_TRACKING = process.env.NEXT_PUBLIC_ENABLE_TRACKING !== 'false'
export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID || ''

// ── Local store pages (MIN-3678) ────────────────────────────────────────────
// Read-side config for minimist.com/store/<handle> and /listing/<id>.
// TYPESENSE_HOST / _PROTOCOL / _PORT and the search-only key are already
// declared above — reuse them rather than adding a second set.
export const TYPESENSE_COLLECTION = process.env.TYPESENSE_COLLECTION ?? 'listings'
// Comma-separated listing statuses the public store pages may show. Empty
// falls back to `sellingInStore` — see lib/local/typesense.ts.
export const LOCAL_LISTING_STATUSES = process.env.LOCAL_LISTING_STATUSES ?? ''
// Comma-separated `store_…` ids to force OFF the public storefront — an
// emergency kill switch, NOT an allowlist (MIN-3745). Empty is the normal
// state: publication is decided by `publication.published` on the store record.
// This can only ever suppress a shop, never publish one. Replaces the old
// LOCAL_STOREFRONT_STORE_IDS allowlist; see lib/local/routes.ts.
export const LOCAL_STOREFRONT_BLOCKED_STORE_IDS =
  process.env.LOCAL_STOREFRONT_BLOCKED_STORE_IDS ?? ''

// Where this app reaches its own route handlers from inside the container.
//
// Middleware cannot call listing-grpc — it runs in the edge runtime, which has
// no HTTP/2 gRPC transport — so it reads the organisation routing vocabulary
// over loopback from `/api/organisation-handles` instead (MIN-5297). Loopback
// rather than the request's own origin: that would leave the pod, cross the
// ingress and come back, making a routing decision depend on external DNS and
// the load balancer being healthy.
//
// This replaced `LOCAL_ORGANISATION_HANDLES`, a comma-separated allowlist that
// was empty in every environment — so an organisation claiming a URL in XL
// routed nowhere until somebody edited a Kubernetes overlay, which nobody ever
// did. See `lib/local/organisation-handles.ts`.
export const INTERNAL_ORIGIN =
  process.env.INTERNAL_ORIGIN ?? `http://127.0.0.1:${process.env.PORT ?? '3000'}`

// listing-grpc, for resolving a store from its public handle.
export const MINIMIST_LISTING__BASE_URL =
  process.env.MINIMIST_LISTING__BASE_URL ?? 'http://localhost:50051'
export const MINIMIST_LISTING__CLIENT_ID = process.env.MINIMIST_LISTING__CLIENT_ID ?? ''
export const MINIMIST_LISTING__CLIENT_SECRET = process.env.MINIMIST_LISTING__CLIENT_SECRET ?? ''
export const MINIMIST_LISTING__AUDIENCE = process.env.MINIMIST_LISTING__AUDIENCE ?? ''
export const MINIMIST_AUTH__TOKEN_URL = process.env.MINIMIST_AUTH__TOKEN_URL ?? ''

// ── Billing (MIN-3953) ──────────────────────────────────────────────────────
// The billing service, for live plan prices on the public pricing page. Only
// `GET /price-quote` is called: pure pricing-curve math, no auth, no tenant.
// Server-only (never NEXT_PUBLIC) — the browser talks to our own
// /api/pricing/quote, which proxies this.
// Trailing slash stripped: an operator setting `https://billing.mnm.st/` would
// otherwise produce `https://billing.mnm.st//price-quote`, which only surfaces
// later as a caught `quote_unreachable` with no hint at the cause.
export const BILLING_API_URL = (process.env.BILLING_API_URL ?? 'https://billing.mnm.st').replace(
  /\/+$/,
  '',
)

// ── Embeddable shop window (MIN-5083) ───────────────────────────────────────
// The `/embed/shop/<handle>` page a customer's own site puts in an <iframe>.
//
// Both of these are DEMO-SHAPED stand-ins for per-store data, and both fail
// closed when unset. See the header comment in lib/local/embed.ts for what
// replaces them (MIN-5081's Shop Scout entitlement, MIN-5082's
// `Store.embed_origins`) and why an env allowlist must not outlive the demo.

// Comma-separated `store_…` ids this deployment will serve an embed for. An
// ALLOWLIST, unlike LOCAL_STOREFRONT_BLOCKED_STORE_IDS above — but one that can
// only ever narrow publication, never grant it, so it is not a second consent
// surface. Empty means no shop is embeddable, which is the correct default.
export const EMBED_ENABLED_STORE_IDS = process.env.EMBED_ENABLED_STORE_IDS ?? ''

// Space- or comma-separated origins allowed to frame the embed, e.g.
// `https://www.thecharity.org.uk`. Empty means `'self'` — the site-wide
// default, i.e. nobody. Sanitised by `frameAncestorsDirective` before it
// reaches a header; see next.config.ts.
export const EMBED_FRAME_ANCESTORS = process.env.EMBED_FRAME_ANCESTORS ?? ''

// Absolute origin the embed's links point at, e.g. `https://minimist.com`.
// Empty means same-origin, which is right in production. It exists because a
// frame served from a dev or staging host should still be able to send a
// shopper to the real listing page during a demo.
//
// Validated as a real origin rather than just trimmed: a value missing its
// scheme (`minimist.com`) would be prefixed onto the path and silently produce
// the RELATIVE url `minimist.com/listing/<id>`, which resolves against the
// embedding page and 404s on the customer's own site. Anything carrying
// credentials, a path, a query or a fragment is refused for the same reason —
// the value is a base, and only the origin part of it is ever meaningful.
// Fails closed to same-origin, which is always safe.
function embedLinkBaseUrl(raw: string | undefined): string {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return ''
  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    // The rejected value is NOT logged. One of the things this function refuses
    // is a URL carrying credentials, so the invalid values most worth rejecting
    // are exactly the ones most dangerous to echo into a deployment log. The
    // variable name and the reason are enough to fix it.
    console.warn('[embed] EMBED_LINK_BASE_URL is not a URL, falling back to same-origin')
    return ''
  }
  const bare = parsed.origin === 'null' ? '' : parsed.origin
  const isPlainOrigin =
    (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
    !parsed.username && !parsed.password &&
    (parsed.pathname === '' || parsed.pathname === '/') &&
    !parsed.search && !parsed.hash &&
    Boolean(bare)
  if (!isPlainOrigin) {
    // Again without the value — see above. The failed condition names itself.
    const why = [
      parsed.protocol === 'http:' || parsed.protocol === 'https:' ? null : 'scheme must be http(s)',
      parsed.username || parsed.password ? 'must not carry credentials' : null,
      parsed.pathname && parsed.pathname !== '/' ? 'must not carry a path' : null,
      parsed.search ? 'must not carry a query' : null,
      parsed.hash ? 'must not carry a fragment' : null,
    ].filter(Boolean).join('; ')
    console.warn(
      `[embed] EMBED_LINK_BASE_URL must be a bare http(s) origin (${why || 'unusable origin'}), falling back to same-origin`,
    )
    return ''
  }
  return bare
}

export const EMBED_LINK_BASE_URL = embedLinkBaseUrl(process.env.EMBED_LINK_BASE_URL)
